import { useEffect, useState } from "react";

import { PropertyUnitPicker, usePropertyUnitCascade } from "@/components/PropertyUnitPicker";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { LeaseResource, TenantResource } from "@/lib/api-types";
import { TENANT_STATUS_LABELS } from "@/lib/enum-labels";
import { labelFor } from "@/lib/field-labels";
import { formatMobilePhoneInput } from "@/lib/phone-format";

/**
 * The real "Add tenant" creation flow (Tier A fix round, Fix 1) — `POST
 * /tenants` was fully built, tested, audited and role-gated on the backend
 * since this story shipped, but nothing in the frontend ever called it,
 * leaving the shipped feature completely unusable end-to-end: an Owner could
 * browse an empty tenants list forever with no way to populate it.
 *
 * A single-step dialog, not a multi-step wizard — this story's own AC
 * doesn't ask for `AddPropertyWizard.tsx`'s own two-step shape (a
 * municipality picker with its own "create a new town" sub-flow and a
 * separate units step), so a plain one-step form mirroring Story 5.3's own,
 * simpler original `CreatePropertyDialog` shape (since superseded by the
 * wizard for properties specifically, but still this codebase's own
 * simplest create-dialog precedent) is the right scope here.
 *
 * Captures `full_name`/`mobile_phone` (the only two `POST /tenants` actually
 * requires) plus every other commonly-filled field the AC names that isn't
 * itself a JSON-list column (`additional_occupants`/`pets`/`vehicles`,
 * spec judgement call 2) or an ID-verification field — those are exactly
 * the kind of "fill in once the person is actually settling in" details
 * this dialog's own `TenantRecord.tsx` already lets an Owner edit inline
 * right after creation, the same "create with the essentials, refine on
 * the record page" shape `AddPropertyWizard.tsx` itself follows for
 * anything beyond its own two steps.
 *
 * Story 15.9 — `move_in_date` is removed from this form entirely (no
 * replacement field takes its exact place; the backend column/API field
 * stays untouched, same precedent as Story 15.8's own `move_in_date`
 * decision — still real, still editable later via `TenantRecord.tsx`'s own
 * inline edit). In its place: an optional "Lease details" section — the
 * same Property -> Unit cascading picker `AddLeaseDialog.tsx` already uses
 * (now shared via `PropertyUnitPicker.tsx`, judgement call 4), a lease
 * start date (required only once a unit is chosen), an end date, and a
 * monthly rent field.
 *
 * Judgement call 1: this section is deliberately lean — unit/start_date/
 * end_date/monthly_rent_cents only, defaulting the new lease's `status` to
 * `"active"` (never exposed as a choice here) rather than surfacing
 * `AddLeaseDialog.tsx`'s own draft/active radio — a tenant being onboarded
 * with a lease right now is presumably moving in now, not being drafted for
 * a future date. `lease_type`/`notice_period_days` are left for the new
 * lease record's own existing inline-edit affordances afterward, the same
 * "create with the essentials, refine on the record page" shape this
 * dialog's own tenant fields above already follow.
 *
 * On submit: `POST /tenants` always fires first and alone decides whether
 * the form's own inline error shows (unchanged from before this story). If
 * a unit was chosen, two more real, separate, already-existing calls follow
 * in order — `POST /leases` on that unit, then `POST /leases/{id}/tenants`
 * attaching the tenant that was just created — mirroring `AddLeaseDialog
 * .tsx`'s own lease-then-attach orchestration exactly (no new backend
 * route, no atomic transaction: AD-2/Out of scope). Leaving the unit
 * unselected skips both calls entirely and preserves this dialog's
 * pre-15.9 behavior byte for byte: close immediately, hand the new tenant's
 * id to `onCreated`.
 *
 * Judgement call 3: a tenant that's created successfully but whose lease
 * step then fails is never silently reported as a generic failure and never
 * has the fact of its own successful creation discarded — mirroring
 * `AddLeaseDialog.tsx`'s own post-submit "result" view, this dialog shows a
 * real confirmation screen naming exactly which step failed and why,
 * with a `Done` action that still lands the Owner on the real tenant record
 * that now exists. A *fully* successful three-call flow does not pause on
 * that screen at all — mirrors the AC's own "no separate step needed" —
 * it closes and navigates immediately, identical to the no-lease path.
 */

const STATUS_OPTIONS = Object.entries(TENANT_STATUS_LABELS).map(([value, label]) => ({ value, label }));

const CONTACT_METHOD_OPTIONS = [
  { value: "", label: "Unspecified" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "text", label: "Text" },
];

const DEFAULT_STATUS = STATUS_OPTIONS[0]?.value ?? "current";

//: Judgement call 1 — a lease created from this dialog is always `"active"`,
//: never exposed as a choice the way `AddLeaseDialog.tsx`'s own draft/active
//: radio is: a tenant being onboarded with a lease right now is presumably
//: moving in now, not being drafted for a future date.
const NEW_LEASE_STATUS = "active";

interface PartialFailure {
  tenantId: string;
  tenantName: string;
  stage: "lease-failed" | "attach-failed";
  message: string;
}

export function AddTenantDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const [fullName, setFullName] = useState("");
  const [mobilePhone, setMobilePhone] = useState("");
  const [email, setEmail] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [status, setStatus] = useState(DEFAULT_STATUS);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [employer, setEmployer] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  //: The optional "Lease details" section (Story 15.9) — the same
  //: Property -> Unit cascade `AddLeaseDialog.tsx` uses, now shared via
  //: `PropertyUnitPicker.tsx` (judgement call 4).
  const cascade = usePropertyUnitCascade(open);
  const [leaseStartDate, setLeaseStartDate] = useState("");
  const [leaseEndDate, setLeaseEndDate] = useState("");
  const [monthlyRentDollars, setMonthlyRentDollars] = useState("");
  //: Set only once the tenant already exists but the lease-creation or
  //: tenant-attach step then failed (judgement call 3) — never cleared
  //: silently, always its own real confirmation screen naming exactly what
  //: happened, mirroring `AddLeaseDialog.tsx`'s own post-submit "result"
  //: view.
  const [partialFailure, setPartialFailure] = useState<PartialFailure | null>(null);

  // Discarded, never persisted anywhere, if the dialog closes before
  // submitting — mirrors `AddPropertyWizard.tsx`'s own reset-on-close effect.
  useEffect(() => {
    if (!open) {
      setFullName("");
      setMobilePhone("");
      setEmail("");
      setPreferredContactMethod("");
      setStatus(DEFAULT_STATUS);
      setDateOfBirth("");
      setEmployer("");
      setEmergencyContactName("");
      setEmergencyContactPhone("");
      setNotes("");
      setError(undefined);
      cascade.reset();
      setLeaseStartDate("");
      setLeaseEndDate("");
      setMonthlyRentDollars("");
      setPartialFailure(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  //: VC-15.9-04 — a unit chosen without a start date blocks submission
  //: client-side, with a clear inline reason, never a raw backend
  //: rejection (mirrors `AddLeaseDialog.tsx`'s own identical
  //: unit-requires-start-date rule).
  const leaseStartDateMissing = cascade.unitId !== "" && leaseStartDate.trim() === "";
  const canSubmit = fullName.trim() !== "" && mobilePhone.trim() !== "" && !leaseStartDateMissing;

  async function handleSubmit() {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    setPartialFailure(null);

    let created: TenantResource;
    try {
      created = await apiFetch<TenantResource>("/tenants", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName.trim(),
          mobile_phone: mobilePhone.trim(),
          email: email.trim() === "" ? null : email.trim(),
          preferred_contact_method: preferredContactMethod === "" ? null : preferredContactMethod,
          status,
          date_of_birth: dateOfBirth === "" ? null : dateOfBirth,
          employer: employer.trim() === "" ? null : employer.trim(),
          emergency_contact_name: emergencyContactName.trim() === "" ? null : emergencyContactName.trim(),
          emergency_contact_phone: emergencyContactPhone.trim() === "" ? null : emergencyContactPhone.trim(),
          notes: notes.trim() === "" ? null : notes.trim(),
        }),
      });
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
      setIsSubmitting(false);
      return;
    }

    if (cascade.unitId === "") {
      // No lease information was given — this dialog's pre-15.9 behavior,
      // unchanged (VC-15.9-02): close immediately, hand the new tenant's id
      // straight to the caller.
      setIsSubmitting(false);
      onOpenChange(false);
      onCreated(created.id);
      return;
    }

    let lease: LeaseResource;
    try {
      lease = await apiFetch<LeaseResource>("/leases", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          unit_id: cascade.unitId,
          start_date: leaseStartDate,
          status: NEW_LEASE_STATUS,
          end_date: leaseEndDate === "" ? null : leaseEndDate,
          monthly_rent_cents:
            monthlyRentDollars.trim() === "" ? null : Math.round(Number(monthlyRentDollars) * 100),
        }),
      });
    } catch (leaseError) {
      // The tenant already exists — that fact is never discarded here
      // (VC-15.9-05): a real, specific result screen names both the
      // successful tenant creation and the lease step's own real failure
      // reason.
      setIsSubmitting(false);
      setPartialFailure({
        tenantId: created.id,
        tenantName: created.full_name,
        stage: "lease-failed",
        message:
          leaseError instanceof ApiError ? leaseError.message : "Something went wrong. Please try again in a moment.",
      });
      return;
    }

    try {
      await apiFetch<LeaseResource>(`/leases/${lease.id}/tenants`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: created.id }),
      });
    } catch (attachError) {
      // Both the tenant and the lease already exist — only the attach
      // itself failed (VC-15.9-06), reported with the same honesty as the
      // lease-creation failure above.
      setIsSubmitting(false);
      setPartialFailure({
        tenantId: created.id,
        tenantName: created.full_name,
        stage: "attach-failed",
        message:
          attachError instanceof ApiError
            ? attachError.message
            : "Something went wrong. Please try again in a moment.",
      });
      return;
    }

    // All three calls succeeded — no separate confirmation step (AC),
    // identical to the no-lease path above: close and navigate straight to
    // the new tenant's own record page, where Story 15.8's key-facts strip
    // now reflects this real lease (VC-15.9-07).
    setIsSubmitting(false);
    onOpenChange(false);
    onCreated(created.id);
  }

  function handleDoneAfterPartialFailure() {
    if (partialFailure) {
      onOpenChange(false);
      onCreated(partialFailure.tenantId);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={partialFailure ? "Tenant added" : "Add a tenant"}
    >
      {partialFailure ? (
        <div className="mt-4 flex flex-col gap-4">
          <p className="text-body text-ink">{partialFailure.tenantName} was added successfully.</p>
          <p className="text-body text-danger">
            {partialFailure.stage === "lease-failed"
              ? `Their lease could not be created: ${partialFailure.message}`
              : `Their lease was created, but they could not be attached to it: ${partialFailure.message}`}
          </p>
          <p className="text-body text-ink-secondary">
            You can set this up any time from the Add Lease flow on the tenant&apos;s own record page.
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" onClick={handleDoneAfterPartialFailure}>
              Done
            </Button>
          </div>
        </div>
      ) : (
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("full_name")}</span>
          <Input autoFocus value={fullName} onChange={(event) => setFullName(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("mobile_phone")}</span>
          <Input
            type="tel"
            value={mobilePhone}
            onChange={(event) => setMobilePhone(formatMobilePhoneInput(event.target.value))}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("email")}</span>
          <Input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("preferred_contact_method")}</span>
          <Select value={preferredContactMethod} onChange={(event) => setPreferredContactMethod(event.target.value)}>
            {CONTACT_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">Status</span>
          <Select value={status} onChange={(event) => setStatus(event.target.value)}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("date_of_birth")}</span>
          <Input type="date" value={dateOfBirth} onChange={(event) => setDateOfBirth(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("employer")}</span>
          <Input value={employer} onChange={(event) => setEmployer(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("emergency_contact_name")}</span>
          <Input value={emergencyContactName} onChange={(event) => setEmergencyContactName(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("emergency_contact_phone")}</span>
          <Input value={emergencyContactPhone} onChange={(event) => setEmergencyContactPhone(event.target.value)} />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("notes")}</span>
          <Input value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>

        {/*
          Story 15.9's own optional "Lease details" section (spec judgement
          call 2) — a plain-language explanation of what leaving it blank
          means, following this codebase's §4.18-style convention. Leaving
          the unit unselected skips lease creation entirely (VC-15.9-02);
          picking one requires a start date (VC-15.9-04).
        */}
        <div className="flex flex-col gap-4 border-t border-border-hairline pt-4">
          <div className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">Lease details (optional)</span>
            <p className="text-meta text-ink-secondary">
              Add their unit and lease dates now, or skip this and do it later from the lease list.
            </p>
          </div>

          <PropertyUnitPicker
            propertyId={cascade.propertyId}
            unitId={cascade.unitId}
            properties={cascade.properties}
            units={cascade.units}
            onPropertyChange={cascade.handlePropertyChange}
            onUnitChange={cascade.setUnitId}
          />

          <div className="flex flex-col gap-1">
            <label className="flex flex-col gap-1">
              <span className="text-label font-medium text-ink-secondary">{labelFor("start_date")}</span>
              <Input
                type="date"
                value={leaseStartDate}
                onChange={(event) => setLeaseStartDate(event.target.value)}
              />
            </label>
            {leaseStartDateMissing ? (
              <p role="alert" className="text-meta text-danger">
                A lease can&apos;t be created without a start date.
              </p>
            ) : null}
          </div>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("end_date")}</span>
            <Input type="date" value={leaseEndDate} onChange={(event) => setLeaseEndDate(event.target.value)} />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-label font-medium text-ink-secondary">{labelFor("monthly_rent_cents")}</span>
            <Input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              placeholder="0.00"
              value={monthlyRentDollars}
              onChange={(event) => setMonthlyRentDollars(event.target.value)}
            />
          </label>
        </div>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Adding…" : "Add tenant"}
          </Button>
        </div>
      </form>
      )}
    </Dialog>
  );
}
